import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import dayjs from "dayjs";
import { M } from "vite/dist/node/types.d-aGj9QkWt";

import { get } from "@/api";
import { Models } from "@/api/types";
import { cartMeetsMinimumSpendRequirementForReward } from "@/modules/cart";
import { Icon } from "atoms/icon/Icon";
import MembershipsIcon from "atoms/icon/icons/ic_memberships_ol.svg";
import SinglePromoIcon from "atoms/icon/icons/ic_offers_ol.svg";
import RewardsIcon from "atoms/icon/icons/ic_rewards_ol.svg";
import { formatCurrency } from "toolbox/Money";

type CartAvailablePromotionProps = {
  onClick?: () => void;
  textButton?: string;
  title: string | null | undefined;
  totalDiscount?: number;
  expiresOn?: string | null | undefined;
  isMembership?: boolean | false;
  appliesToMembership?: boolean;
  isHighlighted?: boolean | "" | undefined;
  isReward?: boolean | false;
  locationName?: string;
  requiresMinimumCartSpend?: boolean;
  minimumCartSpendAmount?: number;
  cartPreFeeTotal?: number;
  promotion?: Models<"RewardPromo">;
};

export const CartAvailablePromotion = ({
  onClick,
  textButton,
  title,
  totalDiscount,
  expiresOn,
  isMembership,
  appliesToMembership,
  isHighlighted,
  isReward,
  locationName,
  requiresMinimumCartSpend,
  minimumCartSpendAmount,
  cartPreFeeTotal = 0,
  promotion,
}: CartAvailablePromotionProps) => {
  const isSelected = isHighlighted;

  const expiresOnText = (expirationDate: string) => {
    const expiration = dayjs(expirationDate);
    const today = dayjs();
    const expirationInDays = expiration.diff(today, "days");

    if (expirationInDays == 1) {
      return "1 day left to claim!";
    }

    return `${dayjs(expiresOn).fromNow(true)} left to claim!`;
  };

  const getIcon = () => {
    if (isReward) return RewardsIcon;
    if (isMembership) return MembershipsIcon;
    return SinglePromoIcon;
  };

  const handleClick = () => {
    if (requiresMinimumCartSpend && minimumCartSpendAmount && !isSelected) {
      if (cartPreFeeTotal < minimumCartSpendAmount) {
        return;
      }
    }
    onClick?.();
  };

  const { data: reward } = useQuery({
    queryKey: ["reward", promotion?.rewardId],
    queryFn: () =>
      get("/rewards/{rewardId}", { rewardId: promotion?.rewardId ?? "" }),
    enabled: !!promotion?.rewardId,
  });

  const meetsMinSpendRequirement = !isSelected
    ? cartMeetsMinimumSpendRequirementForReward(cartPreFeeTotal, reward)
    : true;

  const renderMinimumCartSpendRequirement = () =>
    requiresMinimumCartSpend &&
    !meetsMinSpendRequirement && (
      <div className="text-center text-[11px] text-secondary">
        Unlock with a cart total of at least{" "}
        {formatCurrency(minimumCartSpendAmount ?? 0)}
      </div>
    );

  const renderTextButton = () =>
    textButton && (
      <div
        className={cx(
          "w-1/5 cursor-pointer text-right text-bold3 ",
          meetsMinSpendRequirement && "text-brand-color",
          !meetsMinSpendRequirement && "text-secondary opacity-70"
        )}
      >
        {textButton}
      </div>
    );

  return (
    <div
      className={cx(
        "flex cursor-pointer flex-col gap-2 rounded border p-3",
        isSelected ? "border-brand-color bg-brand-tint" : "border-light-grey",
        !textButton && "pointer-events-none",
        !meetsMinSpendRequirement && "pointer-events-none"
      )}
    >
      <button
        type="button"
        className={cx("flex w-full  items-center justify-between ")}
        onClick={handleClick}
      >
        <div className="flex flex-1 items-center gap-3">
          <div>
            <Icon
              svg={getIcon()}
              color={isSelected ? "brandColor" : "secondaryText"}
              size="xsmall"
            />
          </div>
          <div className="flex flex-col gap-1 text-left">
            <div className="pr-4 font-sans text-bold2 text-primary">
              {title ?? "Reward hidden"}
            </div>
            {totalDiscount && (
              <div
                className={cx(
                  "text-body2",
                  isSelected ? "text-primary" : "text-secondary"
                )}
              >
                -{formatCurrency(totalDiscount)}
              </div>
            )}
            {appliesToMembership && (
              <div
                className={cx(
                  "text-body3",
                  isSelected ? "text-primary" : "text-secondary"
                )}
              >
                Savings apply to first month of membership only
              </div>
            )}
            {expiresOn && (
              <div className="text-body3 text-red">
                {expiresOnText(expiresOn)}
              </div>
            )}
            {locationName && (
              <div className="text-xs text-red">
                <span className="font-small">Not available at the </span>
                <span className="font-medium">{locationName}</span>
                <span className="font-small"> location</span>
              </div>
            )}
          </div>
        </div>
        {renderTextButton()}
      </button>

      {renderMinimumCartSpendRequirement()}
    </div>
  );
};
